import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Sparkles, X } from 'lucide-react';
import AccountCodeManager from '../../managers/AccountCodeManager';
import { AccountCode } from '../../interfaces/AccountCodeInterface';
import {
  getUpdateMiscEmployeeTaskErrorMessage,
  updateMiscEmployeeTaskAccountCodeAndDescription,
} from '../../api/miscEmployeeTask';

/**
 * V2 port of the legacy UpdateMiscEmployeeTaskModal: lets a permitted admin
 * (editMiscTaskAccountCodeAndDescription) change the cost code and/or description
 * of a misc task on an unsubmitted or submitted timesheet. Amount and project are
 * locked — that is the payroll editor's job. Rendered inside the review console so
 * the `.etrc-*` design tokens apply.
 */

const DESCRIPTION_MAX = 500;

export interface MiscTaskCodeTarget {
  miscEmployeeTaskID: number;
  accountCodeID: number | null;
  miscDescription: string;
  projectName: string;
  altProjectID: string | null;
  amountClaimed: number;
  /** "Wednesday" / "Aug 12" — for the modal subtitle. */
  dow: string;
  dateLabel: string;
}

export interface MiscTaskCodeModalProps {
  target: MiscTaskCodeTarget;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const MISC_COLOR = '#d8852f';

const MiscTaskCodeModal: React.FC<MiscTaskCodeModalProps> = ({ target, onClose, onSaved }) => {
  const [accountCodes, setAccountCodes] = useState<AccountCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [accountCodeID, setAccountCodeID] = useState<number | null>(target.accountCodeID);
  const [description, setDescription] = useState(target.miscDescription.slice(0, DESCRIPTION_MAX));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const codes = await AccountCodeManager.getAccountCodes();
        if (!cancelled) setAccountCodes(codes ?? []);
      } catch {
        if (!cancelled) setLoadError('Could not load account codes. Close and try again.');
      } finally {
        if (!cancelled) setCodesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  /** Active codes, plus the current one if it has since been deactivated so the select stays valid. */
  const options = useMemo(() => {
    const active = accountCodes
      .filter((c) => c.active !== false)
      .sort((a, b) => a.name.localeCompare(b.name));
    if (target.accountCodeID == null || active.some((c) => c.accountCodeID === target.accountCodeID)) return active;
    const current = accountCodes.find((c) => c.accountCodeID === target.accountCodeID);
    return current ? [current, ...active] : active;
  }, [accountCodes, target.accountCodeID]);

  const accountChanged = accountCodeID !== target.accountCodeID && accountCodeID !== null;
  const descriptionChanged = description !== target.miscDescription;
  const canSave = (accountChanged || descriptionChanged) && !saving && !loadError && !codesLoading;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateMiscEmployeeTaskAccountCodeAndDescription({
        miscEmployeeTaskID: target.miscEmployeeTaskID,
        ...(accountChanged ? { accountCodeID: accountCodeID as number } : {}),
        ...(descriptionChanged ? { miscDescription: description.slice(0, DESCRIPTION_MAX) } : {}),
      });
      await onSaved();
      onClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setSaveError(
        status === 403
          ? 'Your role is not allowed to change misc task codes.'
          : getUpdateMiscEmployeeTaskErrorMessage(err)
      );
    } finally {
      setSaving(false);
    }
  };

  const error = loadError ?? saveError;

  return (
    <div className="etrc-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="etrc-mw-modal etrc-mtc-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit misc task code"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="etrc-mw-header">
          <div className="etrc-mw-icon-wrap etrc-mtc-icon-wrap">
            <Sparkles size={22} color={MISC_COLOR} />
          </div>
          <div>
            <div className="etrc-mw-title">Edit Misc Task</div>
            <div className="etrc-mw-subtitle">{target.dow}, {target.dateLabel}</div>
          </div>
          <button type="button" className="etrc-mw-close" onClick={onClose} disabled={saving} title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="etrc-mw-body">
          <div className="etrc-mtc-fixed">
            <span className="etrc-mtc-fixed-label">Project</span>
            <span>
              {target.altProjectID && <span className="etrc-project-badge">{target.altProjectID}</span>}
              {target.projectName}
            </span>
            <span className="etrc-mtc-fixed-label">Claimed</span>
            <span className="etrc-num">${target.amountClaimed.toFixed(2)}</span>
          </div>

          <div className="etrc-mtc-field">
            <label htmlFor="etrc-mtc-code">Cost code</label>
            <select
              id="etrc-mtc-code"
              className="etrc-mtc-input"
              value={accountCodeID ?? ''}
              disabled={codesLoading || !!loadError || saving}
              autoFocus
              onChange={(e) => setAccountCodeID(e.target.value === '' ? null : Number(e.target.value))}
            >
              {codesLoading && <option value="">Loading codes…</option>}
              {!codesLoading && target.accountCodeID == null && <option value="">Select a cost code…</option>}
              {options.map((c) => (
                <option key={c.accountCodeID} value={c.accountCodeID}>
                  {c.altAccountCodeID} · {c.name}{c.active === false ? ' (inactive)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="etrc-mtc-field">
            <label htmlFor="etrc-mtc-desc">Description</label>
            <textarea
              id="etrc-mtc-desc"
              className="etrc-mtc-input"
              rows={3}
              maxLength={DESCRIPTION_MAX}
              value={description}
              disabled={saving}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
            />
            <div className="etrc-mtc-count">{description.length} / {DESCRIPTION_MAX}</div>
          </div>

          {error && (
            <div className="etrc-mtc-error">
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="etrc-mw-footer">
          <button type="button" className="etrc-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="etrc-btn primary" onClick={handleSave} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiscTaskCodeModal;
